import { FC, useRef, useState } from 'react'
import Input from '~/components/Input'
import styles from './StakeSelector.pcss'
import { EarnType } from '~/lib/constants'
import { CONFIG, getChainById, getEarnByType } from '~/lib/config'
import Typography from '~/components/Typography'
import useOutsideClick from '~/hooks/useOutsideClick'

import iconsUniSwap from '~/assets/icons/uniswap.png'
import iconsSushiSwap from '~/assets/icons/sushiswap.png'
import iconsPancakeSwap from '~/assets/icons/pancakeswap.png'
import iconEth from '~/assets/chains/eth-small.png'
import iconBnb from '~/assets/chains/bnb-small.png'
import iconArbitrum from '~/assets/chains/arbitrum-small.png'
import ReactTooltip from 'react-tooltip'
import { MaxButton } from '../MaxButton'

interface Props {
	onAmountChange: (value: string) => void
	onDexChange: (dex: EarnType) => void
	withGetLP?: boolean
	readOnly?: boolean
	earnType: EarnType
	amount: string
	maxAmount?: string
}

const chainsIcons = {
	42161: {
		src: iconArbitrum,
		name: 'Arbitrum',
	},
	4: {
		src: iconEth,
		name: 'Ethereum',
	},
	1: {
		src: iconEth,
		name: 'Ethereum',
	},
	56: {
		src: iconBnb,
		name: 'BNB Chain',
	},
}

const earnsItems = [
	{
		type: EarnType.pancakeSwap,
		token: {
			src: iconsPancakeSwap,
			name: 'Pancake',
		},
		chain: chainsIcons[CONFIG.earns[EarnType.pancakeSwap].chainId],
	},
	{
		type: EarnType.sushiSwap,
		token: {
			src: iconsSushiSwap,
			name: 'Sushi',
		},
		chain: chainsIcons[CONFIG.earns[EarnType.sushiSwap].chainId],
	},
	{
		type: EarnType.uniSwap,
		token: {
			src: iconsUniSwap,
			name: 'Uni',
		},
		chain: chainsIcons[CONFIG.earns[EarnType.uniSwap].chainId],
	},
]

const StakeSelector: FC<Props> = ({
	onAmountChange,
	onDexChange,
	earnType,
	amount,
	maxAmount,
	withGetLP = false,
	readOnly = false,
}) => {
	const earnConfig = getEarnByType(earnType)
	const chainConfig = getChainById(earnConfig.chainId)

	const [isOpen, setIsOpen] = useState<boolean>(false)

	const earnSelector = useRef()
	useOutsideClick(earnSelector, () => setIsOpen(false))

	const onSelect = (earnType: EarnType) => {
		onDexChange(earnType)
		setIsOpen(false)
	}

	const onOpen = () => {
		!isOpen && setIsOpen(true)
	}

	const currentEarn = earnsItems.find(
		(earnsItem) => earnsItem.type == earnType
	)

	function onChange({ target }) {
		const value = target.value
		if (value[0] === '0' && value[1] === '0') {
			return
		}

		if (value === '.' || value === ',') {
			onAmountChange('.')
			return
		}

		if (!value.includes('.') && value.includes(',')) {
			onAmountChange(value.replace(/,/, '.'))
			return
		}

		if (value >= 0) {
			onAmountChange(value)
		}
	}

	return (
		<div className={styles.selector}>
			<Typography color="gray" className={styles.selectorCaption}>
				{earnConfig.name} SIS/{chainConfig.token.symbol} LP
			</Typography>
			<div className={styles.selectorRow}>
				<div className={styles.controls}>
					<div className={styles.controlEarns} onClick={onOpen}>
						<img
							src={currentEarn.token.src}
							alt={currentEarn.token.name}
						/>
						<svg
							fill="none"
							height="24"
							viewBox="0 0 24 24"
							width="24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="m8 10 4 4 4-4"
								stroke="#fff"
								strokeLinecap="square"
								strokeLinejoin="bevel"
								strokeWidth="1.5"
							/>
						</svg>
						{isOpen && (
							<div
								key="earn-selector"
								className={styles.controlEarnsSelector}
								ref={earnSelector}
							>
								<Typography
									color="white"
									size="XS"
									className={
										styles.controlEarnsSelectorCaption
									}
								>
									Select DEX
								</Typography>
								<ul className={styles.controlEarnsItems}>
									{earnsItems.map((earnsItem) => (
										<li
											key={`earn-${earnsItem.type}`}
											className={styles.controlEarnsItem}
											onClick={() =>
												onSelect(earnsItem.type)
											}
											data-tip={true}
											data-for={`earn-${earnsItem.type}`}
										>
											<div
												className={
													styles.controlEarnsItemIcon
												}
											>
												<img
													src={earnsItem.token.src}
													className={
														styles.controlEarnsItemToken
													}
													alt={earnsItem.token.name}
												/>
												<img
													src={earnsItem.chain.src}
													className={
														styles.controlEarnsItemChain
													}
													alt={earnsItem.chain.name}
												/>
											</div>
											<Typography
												size="XS"
												className={
													styles.controlEarnsItemCaption
												}
											>
												{
													CONFIG.earns[earnsItem.type]
														.name
												}
											</Typography>
										</li>
									))}
								</ul>
								{earnsItems.map((earnsItem) => (
									<ReactTooltip
										key={`earn-${earnsItem.type}`}
										id={`earn-${earnsItem.type}`}
										place={'bottom'}
										effect={'float'}
										className={
											styles.controlEarnsItemTooltip
										}
										arrowColor="transparent"
										offset={{
											right: 122,
										}}
									>
										<Typography size="XS">
											You need to switch chain to{' '}
											{earnsItem.chain.name} for use this
											pool
										</Typography>
									</ReactTooltip>
								))}
							</div>
						)}
					</div>
					<Input
						className={styles.controlAmount}
						placeholder="Enter amount"
						pattern="^[+]?\d*(?:[.,]\d*)?$"
						readOnly={readOnly}
						value={amount}
						onChange={onChange}
					/>
					{!readOnly && (
						<MaxButton onClick={() => onAmountChange(maxAmount)} />
					)}
					{withGetLP && earnConfig?.lpToken?.getUrl && (
						<a
							className={styles.controlGetLp}
							href={earnConfig.lpToken.getUrl}
							rel="noopener noreferrer"
							target="_blank"
						>
							Get LP
						</a>
					)}
				</div>
			</div>
		</div>
	)
}

export default StakeSelector
